import { api } from '../../../lib/api';

/**
 * Answer autosave with an offline queue (spec §3.4 step 5): every change gets a growing
 * sequence number and is kept in localStorage until the server accepts it, so a dropped
 * network or a closed app loses nothing. The server ignores older sequence numbers.
 */
interface PendingAnswer {
  itemId: string;
  choice: number | null;
  marked: boolean;
  timeMs: number;
  seq: number;
}

interface SaveReply {
  serverTime: string;
  unitIdx: number;
  unitDeadline: string | null;
  status: string;
}

const key = (attemptId: string) => `ep:answers:${attemptId}`;
const seqKey = (attemptId: string) => `ep:seq:${attemptId}`;

export class AnswerQueue {
  private pending = new Map<string, PendingAnswer>();
  private seq: number;
  private inFlight: Promise<boolean> | null = null;
  private timer: ReturnType<typeof setTimeout> | null = null;

  private onSaved: (r: SaveReply, waiting: number) => void = () => undefined;
  private onError: (waiting: number) => void = () => undefined;

  constructor(private readonly attemptId: string) {
    this.seq = Number(localStorage.getItem(seqKey(attemptId)) ?? Date.now() % 1_000_000);
    try {
      const saved = JSON.parse(localStorage.getItem(key(attemptId)) ?? '[]') as PendingAnswer[];
      for (const a of saved) this.pending.set(a.itemId, a);
    } catch {
      /* corrupt storage: start clean */
    }
  }

  listen(onSaved: (r: SaveReply, waiting: number) => void, onError: (waiting: number) => void) {
    this.onSaved = onSaved;
    this.onError = onError;
  }

  get waiting(): number {
    return this.pending.size;
  }

  private persist(): void {
    localStorage.setItem(key(this.attemptId), JSON.stringify([...this.pending.values()]));
    localStorage.setItem(seqKey(this.attemptId), String(this.seq));
  }

  put(a: Omit<PendingAnswer, 'seq'>): void {
    this.seq += 1;
    this.pending.set(a.itemId, { ...a, seq: this.seq });
    this.persist();
    this.schedule(800);
  }

  private schedule(ms: number): void {
    if (this.timer) clearTimeout(this.timer);
    this.timer = setTimeout(() => void this.flush(), ms);
  }

  /** Best-effort background save. All callers join the same drain, including newer edits. */
  flush(): Promise<boolean> {
    this.inFlight ??= this.drain().finally(() => {
      this.inFlight = null;
    });
    return this.inFlight;
  }

  /** A submit/section transition must never proceed after a failed or incomplete save. */
  async flushRequired(): Promise<void> {
    do {
      if (!(await this.flush())) throw new Error('Answers could not be saved. Please try again.');
    } while (this.pending.size);
  }

  private async drain(): Promise<boolean> {
    try {
      while (this.pending.size) {
        const batch = [...this.pending.values()].slice(0, 300);
        const r = await api<SaveReply>('PUT', `/app/attempts/${this.attemptId}/answers`, {
          answers: batch,
        });
        // An edit made while this batch was in flight needs its own acknowledgement.
        for (const a of batch)
          if (this.pending.get(a.itemId)?.seq === a.seq) this.pending.delete(a.itemId);
        this.persist();
        this.onSaved(r, this.pending.size);
      }
      return true;
    } catch {
      this.onError(this.pending.size);
      this.schedule(5000);
      return false;
    }
  }

  clear(): void {
    if (this.timer) clearTimeout(this.timer);
    this.pending.clear();
    localStorage.removeItem(key(this.attemptId));
    localStorage.removeItem(seqKey(this.attemptId));
  }
}

/** Time spent per question (sent with each answer as `timeMs`). */
export class TimeTracker {
  private current: string | null = null;
  private since = 0;
  private readonly spent = new Map<string, number>();

  show(itemId: string): void {
    this.leave();
    this.current = itemId;
    this.since = Date.now();
  }

  leave(): void {
    if (this.current) this.spent.set(this.current, this.total(this.current));
    this.current = null;
  }

  total(itemId: string): number {
    const base = this.spent.get(itemId) ?? 0;
    const live = this.current === itemId ? Date.now() - this.since : 0;
    return Math.min(4 * 3_600_000, base + live);
  }
}

export const clientNow = (): number => Date.now();
